/**
 * The ⌘K palette's **machines** source.
 *
 * Registered at module scope, as `chrome-oem/OemCommandPalette.tsx` asks
 * sources to be: importing this file anywhere in the app registers the source,
 * and the palette needs no edit. `FleetHealthScene` and `MachineScene` both
 * import it for the side effect, so it is live on any route the router loads.
 *
 * Matching is the demo's ⌘K behaviour: id, model or site name, case-insensitive
 * substring. Ranking puts an id prefix first (typing `L-0` should offer `L-01`
 * before a loader at a site whose name contains `l0`), then id substring, then
 * everything else in fleet order.
 *
 * Pure and synchronous — it runs on every keystroke and reads only the eager
 * `FLEET` array, never a fetch.
 *
 * @see src/versions/v1/chrome-oem/OemCommandPalette.tsx
 */

import { Truck } from 'lucide-react';
import { FLEET, MINES } from '../../data/core';
import { registerPaletteSource } from '../../chrome-oem/OemCommandPalette';
import { CLSNAME } from '../../shared/oem-engine';

/** How many machines the palette offers at once. */
const LIMIT = 8;

registerPaletteSource({
  id: 'machines',
  label: 'Machines',
  order: 10,
  search: (query) => {
    const q = query.trim().toLowerCase();

    const matches = q
      ? FLEET.filter(
          (f) =>
            f.machine_id.toLowerCase().includes(q) ||
            f.model.toLowerCase().includes(q) ||
            MINES[f.mine].name.toLowerCase().includes(q),
        ).sort((a, b) => rank(a.machine_id, q) - rank(b.machine_id, q))
      : // Empty query: the palette wants a short default list, not 100 rows.
        FLEET.slice(0, LIMIT);

    return matches.slice(0, LIMIT).map((f) => ({
      id: f.machine_id,
      label: f.machine_id,
      hint: `${f.model} · ${CLSNAME[f.cls]} · ${MINES[f.mine].name}`,
      icon: Truck,
      to: `/machines/${encodeURIComponent(f.machine_id)}`,
    }));
  },
});

/** 0 = id starts with the query, 1 = id contains it, 2 = matched elsewhere. */
function rank(machineId: string, q: string): number {
  const id = machineId.toLowerCase();
  if (id.startsWith(q)) return 0;
  if (id.includes(q)) return 1;
  return 2;
}
